import { Box } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import ChartSection from '../shared/ChartSection';
import ChartTooltip from '../shared/ChartTooltip';
import useNivoTheme from '../shared/useNivoTheme';

const lastNameOf = full => {
    const parts = (full || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '—';
    return parts[parts.length - 1];
};

export default function TradingTimeline({ data }) {
    const { theme } = useNivoTheme();
    if (!data?.data || !data?.traders || data.data.length === 0) return null;

    // Disambiguate last-name collisions with a first initial.
    const lastCount = {};
    data.traders.forEach(n => {
        const last = lastNameOf(n);
        lastCount[last] = (lastCount[last] || 0) + 1;
    });

    const legendLabel = name => {
        const parts = name.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '—';
        if (parts.length === 1) return parts[0];
        const last = parts[parts.length - 1];
        if (lastCount[last] > 1) return parts[0][0] + '. ' + last;
        return last;
    };

    const lineData = data.traders.map(name => ({
        id: legendLabel(name),
        data: data.data.map(d => ({ x: d.month, y: d[name] || 0 })),
    }));

    const colorScheme = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

    return (
        <ChartSection
            id="trading-timeline"
            title="Who's been trading the most recently?"
            subtitle="Monthly trade count for the 5 most active members over time (stacked)"
        >
            <Box sx={{ height: 360 }}>
                <ResponsiveLine
                    data={lineData}
                    margin={{ top: 20, right: 130, bottom: 60, left: 55 }}
                    xScale={{ type: 'point' }}
                    yScale={{ type: 'linear', min: 0, max: 'auto', stacked: true }}
                    curve="monotoneX"
                    enablePoints={false}
                    enableGridX={false}
                    colors={colorScheme}
                    lineWidth={2}
                    enableArea
                    areaOpacity={0.15}
                    axisBottom={{
                        tickRotation: 45,
                        tickValues: data.data.filter((_, i) => i % 12 === 0).map(d => d.month),
                    }}
                    axisLeft={{
                        legend: 'Trades (stacked)',
                        legendPosition: 'middle',
                        legendOffset: -40,
                    }}
                    legends={[
                        {
                            anchor: 'top-right',
                            direction: 'column',
                            translateX: 110,
                            itemWidth: 100,
                            itemHeight: 20,
                            symbolSize: 8,
                            itemTextColor: '#9ca3af',
                        },
                    ]}
                    enableSlices="x"
                    sliceTooltip={({ slice }) => (
                        <ChartTooltip
                            title={String(slice.points[0].data.x)}
                            rows={slice.points.map(p => ({
                                label: p.seriesId,
                                value: Number(p.data.y).toLocaleString() + ' trades',
                                color: p.seriesColor,
                            }))}
                        />
                    )}
                    theme={theme}
                />
            </Box>
        </ChartSection>
    );
}
